// ═══════════════════════════════════════════════════════════════
// Registro de dashboards del módulo Mi negocio (2-oct-2026).
//
// Los tableros de acá no son de UN canal: miran el negocio entero y comparan
// canales. Por eso declaran `ambito: 'mi-negocio'` en vez de `plataforma`; el
// ámbito (`AMBITOS` de `@core/lib/dashboardsRegistro`) dice qué canales tienen
// que estar prendidos y en el plan, y la ficha de la empresa sólo muestra la
// solapa con esos canales conectados.
//
// Shape de entrada: el contrato del agregador neutro (ver la cabecera de
// `mercadolibre/registry.js`), con `ambito` en lugar de `plataforma`. La lista
// la fija `registry.test.js`; ampliarla = sumar acá + al test.
// ═══════════════════════════════════════════════════════════════
import { Scale, Users, PackagePlus, LayoutDashboard, Crosshair } from 'lucide-react'

export const DASHBOARDS_MI_NEGOCIO = [
  {
    // El Pulso del negocio (6-oct-2026): Mercado Libre y la tienda cara a
    // cara, con la estética de Cómo compran. Lo calcula la misma corrida que
    // Precios, aislado y con su flag. `soloStaff` hasta que se abra a clientes
    // (como el Pulso de la tienda); primero de la solapa.
    id: 'mn-pulso',
    nombre: 'Pulso del negocio',
    bajada: 'Mirá tus dos canales juntos: cuánto vende cada uno y qué oportunidades aparecen.',
    descripcion: 'Tus dos canales juntos, cara a cara: lo que vendió cada uno, cuánto pesa cada uno en el total, las oportunidades que aparecen al mirarlos juntos (lo que falta publicar, la tienda sin stock, los precios) y cómo está cada canal contra sí mismo.',
    ambito: 'mi-negocio',
    familia: 'general',
    tipo: 'informacion',
    icono: '📊',
    Icon: LayoutDashboard,
    component: () => import('./dashboards/PulsoNegocioDashboard.jsx'),
    orden: -1,
    soloStaff: true,
  },
  {
    id: 'mn-precios',
    nombre: 'Precios',
    bajada: 'Compará tu oferta en Mercado Libre y en tu tienda, y mirá qué conviene hacer.',
    descripcion: 'Para cada producto que vendés en los dos canales: qué oferta le conviene más al comprador (precio, envío, cuotas, entrega y la confianza de cada canal), cuánto te queda en cada uno, qué costaría igualar la oferta y hasta dónde conviene acercar el precio.',
    ambito: 'mi-negocio',
    familia: 'precios-promociones',
    tipo: 'informacion',
    icono: '⚖️',
    Icon: Scale,
    component: () => import('./dashboards/PreciosDashboard.jsx'),
    orden: 0,
  },
  {
    // El aprendizaje del vendedor (3-oct-2026): lo calcula la misma corrida
    // que Precios, que lo usa para completar la oferta.
    id: 'mn-compras',
    nombre: 'Cómo compran tus clientes',
    bajada: 'Mirá cómo pagan, cómo les llega y desde dónde compran en cada canal.',
    descripcion: 'Lo que dicen tus ventas de Mercado Libre y de tu tienda, lado a lado: contado o cuotas, cuotas sin interés, con qué pagan, envío gratis o cuánto pagan de envío, cuánto tarda en llegar y desde qué provincias compran. Es lo que usa Precios para completar la oferta cuando la publicación no trae el dato.',
    ambito: 'mi-negocio',
    familia: 'clientes',
    tipo: 'informacion',
    icono: '🧾',
    Icon: Users,
    component: () => import('./dashboards/ComoCompranDashboard.jsx'),
    orden: 1,
  },
  {
    // Geo Targeting (8-oct-2026, nombre de Mariano): dónde están las personas
    // que compran por cada canal y qué compran ahí, para elegir dónde y qué
    // anunciar. Lee los docs __geo del servidor (flag feature.mi-negocio.geo
    // por empresa); sin la llave dice «todavía no se calculó».
    id: 'mn-geo-targeting',
    nombre: 'Geo Targeting',
    bajada: 'Mirá dónde están las personas que te compran por cada canal, y qué te compran, para anunciar.',
    descripcion: 'Las provincias cara a cara en compradores distintos (no en ventas), con todas las que llegan a 3 personas. Al tocar una, sus localidades y los productos que más se venden ahí por canal. Dónde pautar primero: las zonas y los productos donde te compran por Mercado Libre y no por tu tienda. Y las listas de zonas para pegar en Meta y Google.',
    ambito: 'mi-negocio',
    familia: 'clientes',
    tipo: 'informacion',
    icono: '🎯',
    Icon: Crosshair,
    component: () => import('./dashboards/GeoTargetingDashboard.jsx'),
    orden: 1.5,
  },
  {
    // Lo que está en un canal y no en el otro (3-oct-2026): lee las mismas
    // filas que Precios (la misma corrida), con los filtros de la matriz.
    id: 'mn-publicar',
    nombre: 'Para publicar',
    bajada: 'Mirá qué vendés en un canal y todavía no publicaste en el otro, por matriz.',
    descripcion: 'Lo que tenés en Mercado Libre y no en tu tienda, y al revés, con los filtros de la matriz (estrella, moderada, eventual, incógnita) para ver primero lo que más se vende. Los que tienen uno parecido en el otro canal van aparte, para revisar. Cada producto se puede sembrar en Productos desde acá.',
    ambito: 'mi-negocio',
    familia: 'catalogo',
    // 'informacion' y no 'acciones': lo que se hace desde acá es sembrar
    // productos, no generar tareas (la definición del tipo en el registro de
    // ML). Con 'acciones' el Hub le ponía la pastilla de tareas a una pantalla
    // que no crea ninguna.
    tipo: 'informacion',
    icono: '📤',
    Icon: PackagePlus,
    component: () => import('./dashboards/ParaPublicarDashboard.jsx'),
    orden: 2,
  },
]
